// windPrefsStore — wind-particle layer preference state.
//
// Extracted from uiStore.ts as step 2c of HI-2 (audit 2026-05-22).
// The 4 wind-particle pref fields + their setters moved here so the
// uiStore can shrink one bucket at a time.
//
// Persistence: reads/writes the shared `fyrefly.ui.v1` localStorage blob
// via the same prefsSync helpers uiStore uses. Critically, wind prefs
// persist into a NESTED `prefs.wind.{altitudeFt, forecastHour,
// hideOnLowEnd, autoOffAirborne}` sub-object — NOT as flat top-level
// keys like the traffic and instrument prefs. This nested shape was
// established when the wind-particles feature shipped (2026-05-20) and
// must be preserved verbatim so existing users' persisted wind prefs
// continue to load after this refactor. No localStorage key namespace
// changes — `fyrefly.ui.v1` key is unchanged.
//
// Cloud-sync: the PersistedUiPrefs schema in prefsSync.ts already holds
// the `wind` sub-object. On pull (sign-in), prefsSync.ts applies wind
// fields to this store via a lazy import. No Supabase schema migration
// needed — the `prefs` jsonb column is schema-less.
//
// Uses subscribeWithSelector middleware so consumers can do fine-grained
// field-level subscriptions (audit synthesis HI-2 goal).

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { loadUiPrefs, persistUiPrefs } from './prefsSync';

// ── Types ────────────────────────────────────────────────────────────

export interface WindPrefsState {
  /**
   * Altitude slider value (MSL ft). Snaps to the stops defined in
   * WindAltitudeControl (0, 1500, 3000, 5K, 8K, 10K, 12K, 16K, 18K,
   * 24K, 30K, 34K, 40K ft). Default 5 000 ft — typical air-attack /
   * low-cruise altitude for fire ops.
   */
  windAltitudeFt: number;
  setWindAltitudeFt: (alt: number) => void;

  /**
   * Forecast hour for the ±24h GFS scrubber. Snaps to GFS hours
   * 0 / 3 / 6 / 9 / 12 / 15 / 18 / 21 / 24. 0 = "NOW" (analysis
   * field, most accurate for current conditions). Default 0.
   */
  windForecastHour: number;
  setWindForecastHour: (hour: number) => void;

  /**
   * Hide the wind layer on low-end devices. Defaults to false.
   * Set to true if the user manually opts out via the layer-sheet
   * detail. Can also be set automatically on first mount when we
   * detect a device that would struggle to render the particles.
   */
  windHideOnLowEnd: boolean;
  setWindHideOnLowEnd: (v: boolean) => void;

  /**
   * Auto-disable the wind particle layer once we're airborne (state
   * becomes 'armed' or 'airborne'). The layer is a pre-flight planning
   * aid; in flight it's a battery + GPU drain with no operational
   * value. Defaults to true — audit 2026-05-22 CR-1.
   * Pilot can override by setting this to false in Settings.
   */
  windAutoOffAirborne: boolean;
  setWindAutoOffAirborne: (v: boolean) => void;
}

// ── Initialization ───────────────────────────────────────────────────

// Hydrate from the shared prefs blob so the first render already
// reflects the user's saved choices (no default-then-snap flicker).
// Wind prefs live in the nested `prefs.wind` sub-object.
const _prefs = loadUiPrefs();

// ── Store ────────────────────────────────────────────────────────────

export const useWindPrefsStore = create<WindPrefsState>()(
  subscribeWithSelector((set) => ({
    // Default to 5K — typical air-attack / low-cruise altitude for fire ops.
    windAltitudeFt: _prefs.wind?.altitudeFt ?? 5000,
    setWindAltitudeFt: (alt) => {
      set({ windAltitudeFt: alt });
      // Persist into the NESTED wind sub-object — must spread the existing
      // wind keys so we don't clobber forecastHour / hideOnLowEnd / etc.
      persistUiPrefs({ wind: { ...loadUiPrefs().wind, altitudeFt: alt } });
    },

    windForecastHour: _prefs.wind?.forecastHour ?? 0,
    setWindForecastHour: (hour) => {
      set({ windForecastHour: hour });
      persistUiPrefs({ wind: { ...loadUiPrefs().wind, forecastHour: hour } });
    },

    windHideOnLowEnd: _prefs.wind?.hideOnLowEnd ?? false,
    setWindHideOnLowEnd: (v) => {
      set({ windHideOnLowEnd: v });
      persistUiPrefs({ wind: { ...loadUiPrefs().wind, hideOnLowEnd: v } });
    },

    // Default ON — auto-disable in-flight is the right behavior for 95%
    // of fire-aviation ops (wind particles are a pre-flight planning aid,
    // not a tactical in-flight tool).
    windAutoOffAirborne: _prefs.wind?.autoOffAirborne ?? true,
    setWindAutoOffAirborne: (v) => {
      set({ windAutoOffAirborne: v });
      persistUiPrefs({ wind: { ...loadUiPrefs().wind, autoOffAirborne: v } });
    },
  })),
);
